import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import type { ConflictError } from '@eis/core';

/**
 * "Daten wurden geändert" — the one thing the console does when it loses a race.
 *
 * Every mutating RPC on this platform carries a precondition (decision T-12), so somebody else
 * having changed the policy is a normal outcome, not an exception. The rule that follows is short
 * and absolute: NEVER retry. A retry would re-send the write with a fresh precondition, which is
 * a machine deciding to overwrite the other operator — exactly what optimistic concurrency is
 * there to prevent.
 *
 * `useServiceMutation` has already invalidated the queries, so the screen behind this notice is
 * showing the current state by the time it renders. What is left is a decision, and the decision
 * is the user's: look at what changed, then act again if they still want to.
 *
 * It is one component rather than a message per form because the answer must be identical
 * everywhere. A form that phrased it differently would invite the reader to think something
 * different had happened.
 */
export function ConflictNotice({
  conflict,
  onReload,
}: {
  readonly conflict: ConflictError | null;
  readonly onReload?: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  if (conflict === null) return null;

  return (
    <Alert
      severity="warning"
      data-testid="conflict-notice"
      action={
        onReload === undefined ? undefined : (
          <Button color="inherit" size="small" onClick={onReload}>
            {t('policies.conflict.reload')}
          </Button>
        )
      }
    >
      <AlertTitle>{t('policies.conflict.title')}</AlertTitle>
      {/* Two different losses, two different sentences: a write that never read at all is a
          different mistake from one whose read went out of date (§B.10). */}
      {conflict.kind === 'precondition-required'
        ? t('policies.conflict.noPrecondition')
        : t('policies.conflict.description')}
    </Alert>
  );
}
